import * as THREE from "three";
import { FullScreenQuad } from "three/examples/jsm/postprocessing/Pass.js";

/**
 * The game's look after the scene is drawn: bloom on the things that glow, ACES tone mapping, a little extra colour,
 * a soft vignette and SMAA — plus the quality tiers that decide whether any of it (and the real-time shadows) runs.
 *
 *  high: HDR scene → bloom → grade (tone map, saturation, vignette, dither) → SMAA → screen; sun shadow map on.
 *  low:  the scene straight to the screen (tone mapped in its shaders, MSAA from the context), blob shadows only.
 */

export type Quality = "high" | "low";

/** Everything brighter than this (linear, before exposure) blooms: lights, flames, coin glows, the sun. */
export const BLOOM_THRESHOLD = 1.1;

/**
 * Two tiers. high (the default on PCs and decent phones): HDR post (bloom, grade, SMAA), the sun's shadow map,
 * pixel ratio up to 1.5, no canvas MSAA (SMAA does the edges). low (genuinely weak phones only): straight to the
 * screen, blob shadows, canvas MSAA, pixel ratio up to 1.25. `?quality=high|low` pins one. Decided before the
 * renderer exists, because MSAA is a context attribute.
 */
export function pickQuality(): { quality: Quality; forced: boolean } {
  const asked = new URLSearchParams(location.search).get("quality");
  if (asked === "high" || asked === "low") return { quality: asked, forced: true };
  const dpr = window.devicePixelRatio || 1;
  const touch = matchMedia("(pointer: coarse)").matches;
  const cores = navigator.hardwareConcurrency || 4;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const weakPhone = touch && (cores <= 4 || memory <= 3 || dpr < 1.5);
  return { quality: weakPhone ? "low" : "high", forced: false };
}

/**
 * Dynamic resolution, the only automatic downgrade and a last resort: the render scale drops 1 → 0.8 only after two
 * rolling 1.5 s windows in a row average over 20 ms, and returns after 4 s under 13 ms; if it had to drop again within
 * 10 s of returning, it stays at 0.8 (no thrashing). Gaps over half a second (tab switches, compiles) are ignored.
 */
export class DynamicResolution {
  static readonly STEPS = [0.8, 1];
  private step = 1;
  private ceiling = 1;
  private slowWindows = 0;
  private last = 0;
  private windowStart = 0;
  private frames = 0;
  private sum = 0;
  private fastFor = 0;
  private steppedUpAt = -1e9;
  /** the last window's frames per second (dev log) */
  fps = 0;

  get scale(): number { return DynamicResolution.STEPS[this.step]; }

  /** Feed once per frame with performance.now(); true when the scale changed. */
  tick(now: number): boolean {
    const gap = this.last > 0 ? now - this.last : 0;
    this.last = now;
    if (gap <= 0 || gap > 500) { this.windowStart = now; this.frames = 0; this.sum = 0; return false; }
    this.frames++;
    this.sum += gap;
    if (now - this.windowStart < 1500) return false;
    const average = this.sum / this.frames;
    const span = (now - this.windowStart) / 1000;
    this.fps = 1000 / average;
    this.windowStart = now; this.frames = 0; this.sum = 0;
    if (average > 20) {
      this.fastFor = 0;
      if (++this.slowWindows < 2 || this.step === 0) return false;
      this.slowWindows = 0;
      if (now - this.steppedUpAt < 10000) this.ceiling = this.step - 1;
      this.step--;
      return true;
    }
    this.slowWindows = 0;
    this.fastFor = average < 13 ? this.fastFor + span : 0;
    if (this.fastFor >= 4 && this.step < this.ceiling) {
      this.fastFor = 0;
      this.step++;
      this.steppedUpAt = now;
      return true;
    }
    return false;
  }
}

/** CPU copy of three's ACESFilmicToneMapping (r180): colours that are mixed in after the tone map (fog, clear colour on low) are matched with it. */
export function acesFilmic(color: THREE.Color, exposure: number, target = new THREE.Color()): THREE.Color {
  const r0 = color.r * exposure / 0.6, g0 = color.g * exposure / 0.6, b0 = color.b * exposure / 0.6;
  const r1 = 0.59719 * r0 + 0.35458 * g0 + 0.04823 * b0;
  const g1 = 0.07600 * r0 + 0.90834 * g0 + 0.01566 * b0;
  const b1 = 0.02840 * r0 + 0.13383 * g0 + 0.83777 * b0;
  const fit = (v: number) => (v * (v + 0.0245786) - 0.000090537) / (v * (0.983729 * v + 0.4329510) + 0.238081);
  const r2 = fit(r1), g2 = fit(g1), b2 = fit(b1);
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  return target.setRGB(
    clamp(1.60475 * r2 - 0.53108 * g2 - 0.07367 * b2),
    clamp(-0.10208 * r2 + 1.10813 * g2 - 0.00605 * b2),
    clamp(-0.00327 * r2 - 0.07276 * g2 + 1.07602 * b2),
    THREE.LinearSRGBColorSpace
  );
}

/**
 * A daylight sky for image-based lighting: blue overhead, a pale horizon, a warm ground bounce and a bright sun,
 * prefiltered with PMREM so physical materials (the roadster's paint and glass, the Kenney kits) get soft reflections.
 */
export function skyEnvironment(renderer: THREE.WebGLRenderer, sunDirection: THREE.Vector3, colors: { top: THREE.Color; horizon: THREE.Color; ground: THREE.Color; sun: THREE.Color }): THREE.WebGLRenderTarget {
  const scene = new THREE.Scene();
  const material = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: {
      top: { value: colors.top }, horizon: { value: colors.horizon }, ground: { value: colors.ground },
      sunColor: { value: colors.sun }, sunDirection: { value: sunDirection.clone().normalize() }
    },
    vertexShader: "varying vec3 vDir; void main(){ vDir = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }",
    fragmentShader: `
      uniform vec3 top; uniform vec3 horizon; uniform vec3 ground; uniform vec3 sunColor; uniform vec3 sunDirection;
      varying vec3 vDir;
      void main() {
        vec3 d = normalize(vDir);
        vec3 sky = mix(horizon, top, pow(clamp(d.y, 0.0, 1.0), 0.6));
        vec3 below = mix(horizon * 0.8, ground, clamp(-d.y * 4.0, 0.0, 1.0));
        vec3 color = d.y >= 0.0 ? sky : below;
        float s = max(dot(d, sunDirection), 0.0);
        color += sunColor * (pow(s, 900.0) * 40.0 + pow(s, 12.0) * 0.4);
        gl_FragColor = vec4(color, 1.0);
      }`
  });
  const sphere = new THREE.Mesh(new THREE.SphereGeometry(50, 48, 24), material);
  scene.add(sphere);
  const generator = new THREE.PMREMGenerator(renderer);
  const target = generator.fromScene(scene, 0.02, 0.1, 200);
  generator.dispose();
  sphere.geometry.dispose();
  material.dispose();
  return target;
}

const FULLSCREEN_VERTEX = /* glsl */`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }`;

/** Bright-pass + 4-tap downsample from the full-size scene straight to a quarter-size target (soft knee, no popping). */
const PREFILTER = /* glsl */`
  uniform sampler2D tScene; uniform vec2 texel; uniform float threshold; uniform float knee;
  varying vec2 vUv;
  void main() {
    vec3 a = texture2D(tScene, vUv + texel * vec2(-1.0, -1.0)).rgb, b = texture2D(tScene, vUv + texel * vec2(1.0, -1.0)).rgb;
    vec3 d = texture2D(tScene, vUv + texel * vec2(-1.0, 1.0)).rgb, e = texture2D(tScene, vUv + texel * vec2(1.0, 1.0)).rgb;
    float wa = 1.0 / (1.0 + max(a.r, max(a.g, a.b))), wb = 1.0 / (1.0 + max(b.r, max(b.g, b.b)));
    float wd = 1.0 / (1.0 + max(d.r, max(d.g, d.b))), we = 1.0 / (1.0 + max(e.r, max(e.g, e.b)));
    vec3 c = (a * wa + b * wb + d * wd + e * we) / (wa + wb + wd + we);
    float bright = max(c.r, max(c.g, c.b));
    float soft = clamp(bright - threshold + knee, 0.0, 2.0 * knee);
    soft = soft * soft / (4.0 * knee + 1e-4);
    float weight = max(soft, bright - threshold) / max(bright, 1e-4);
    gl_FragColor = vec4(min(c * weight, vec3(16.0)), 1.0);
  }`;

/** Dual-filter (Kawase) downsample: 5 bilinear taps. */
const DOWN = /* glsl */`
  uniform sampler2D tSource; uniform vec2 texel;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(tSource, vUv).rgb * 4.0;
    c += texture2D(tSource, vUv + texel * vec2(-1.0, -1.0)).rgb + texture2D(tSource, vUv + texel * vec2(1.0, -1.0)).rgb;
    c += texture2D(tSource, vUv + texel * vec2(-1.0, 1.0)).rgb + texture2D(tSource, vUv + texel * vec2(1.0, 1.0)).rgb;
    gl_FragColor = vec4(c / 8.0, 1.0);
  }`;

/** Dual-filter upsample: 8 bilinear taps, added onto the next larger level (additive blending). */
const UP = /* glsl */`
  uniform sampler2D tSource; uniform vec2 texel; uniform float weight;
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(tSource, vUv + texel * vec2(-2.0, 0.0)).rgb + texture2D(tSource, vUv + texel * vec2(2.0, 0.0)).rgb;
    c += texture2D(tSource, vUv + texel * vec2(0.0, -2.0)).rgb + texture2D(tSource, vUv + texel * vec2(0.0, 2.0)).rgb;
    c += 2.0 * (texture2D(tSource, vUv + texel * vec2(-1.0, -1.0)).rgb + texture2D(tSource, vUv + texel * vec2(1.0, -1.0)).rgb);
    c += 2.0 * (texture2D(tSource, vUv + texel * vec2(-1.0, 1.0)).rgb + texture2D(tSource, vUv + texel * vec2(1.0, 1.0)).rgb);
    gl_FragColor = vec4(c / 12.0 * weight, 1.0);
  }`;

/**
 * The one full-size post pass: FXAA on the HDR scene (edges found on compressed luma), + bloom, ACES tone map (the
 * renderer's exposure), sRGB, a little extra saturation and contrast, a light vignette, dither against banding.
 */
const FINAL = /* glsl */`
  uniform sampler2D tScene; uniform sampler2D tBloom; uniform vec2 texel; uniform float bloomStrength;
  uniform float saturation; uniform float contrast; uniform float vignette; uniform float aspect; uniform float fxaa;
  #include <tonemapping_pars_fragment>
  varying vec2 vUv;
  float lumaOf(vec3 c) { float l = dot(c, vec3(0.299, 0.587, 0.114)); return sqrt(l / (1.0 + l)); }
  void main() {
    vec3 rgbM = texture2D(tScene, vUv).rgb;
    vec3 rgbNW = texture2D(tScene, vUv + texel * vec2(-1.0, -1.0)).rgb;
    vec3 rgbNE = texture2D(tScene, vUv + texel * vec2(1.0, -1.0)).rgb;
    vec3 rgbSW = texture2D(tScene, vUv + texel * vec2(-1.0, 1.0)).rgb;
    vec3 rgbSE = texture2D(tScene, vUv + texel * vec2(1.0, 1.0)).rgb;
    float lM = lumaOf(rgbM), lNW = lumaOf(rgbNW), lNE = lumaOf(rgbNE), lSW = lumaOf(rgbSW), lSE = lumaOf(rgbSE);
    float lMin = min(lM, min(min(lNW, lNE), min(lSW, lSE)));
    float lMax = max(lM, max(max(lNW, lNE), max(lSW, lSE)));
    vec3 color = rgbM;
    if (fxaa > 0.5 && lMax - lMin > max(0.0312, lMax * 0.125)) {
      vec2 dir = vec2(-((lNW + lNE) - (lSW + lSE)), (lNW + lSW) - (lNE + lSE));
      float reduce = max((lNW + lNE + lSW + lSE) * 0.03125, 1.0 / 128.0);
      float rcpMin = 1.0 / (min(abs(dir.x), abs(dir.y)) + reduce);
      dir = clamp(dir * rcpMin, vec2(-8.0), vec2(8.0)) * texel;
      vec3 rgbA = 0.5 * (texture2D(tScene, vUv + dir * (1.0 / 3.0 - 0.5)).rgb + texture2D(tScene, vUv + dir * (2.0 / 3.0 - 0.5)).rgb);
      vec3 rgbB = rgbA * 0.5 + 0.25 * (texture2D(tScene, vUv - dir * 0.5).rgb + texture2D(tScene, vUv + dir * 0.5).rgb);
      float lB = lumaOf(rgbB);
      color = (lB < lMin || lB > lMax) ? rgbA : rgbB;
    }
    color += texture2D(tBloom, vUv).rgb * bloomStrength;
    vec4 outColor = sRGBTransferOETF(vec4(ACESFilmicToneMapping(color), 1.0));
    float luma = dot(outColor.rgb, vec3(0.2126, 0.7152, 0.0722));
    outColor.rgb = mix(vec3(luma), outColor.rgb, saturation);
    outColor.rgb = (outColor.rgb - 0.5) * contrast + 0.5;
    vec2 p = (vUv - 0.5) * vec2(aspect, 1.0);
    float r = length(p) / length(vec2(aspect, 1.0) * 0.5);
    outColor.rgb *= 1.0 - vignette * smoothstep(0.4, 1.05, r);
    outColor.rgb += (fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) / 255.0;
    gl_FragColor = vec4(clamp(outColor.rgb, 0.0, 1.0), 1.0);
  }`;

/**
 * The high-quality frame in as few full-size passes as possible (the game is fill-rate bound on integrated GPUs):
 * the scene into one HDR target, a bloom chain at a quarter size and smaller (prefilter → 3 × down → 3 × up, about
 * a sixth of one full-size pass in all), then ONE full-size pass to the screen (FXAA + bloom + tone map + grade).
 */
export class PostFX {
  private scene: THREE.WebGLRenderTarget;
  private levels: THREE.WebGLRenderTarget[] = [];
  private quad = new FullScreenQuad();
  private prefilter: THREE.ShaderMaterial;
  private down: THREE.ShaderMaterial;
  private up: THREE.ShaderMaterial;
  private final: THREE.ShaderMaterial;
  private width = 1;
  private height = 1;

  constructor(private renderer: THREE.WebGLRenderer, private world: THREE.Scene, private camera: THREE.Camera) {
    const target = (depth: boolean, samples = 0) => new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: depth, stencilBuffer: false, generateMipmaps: false, samples });
    // 4× MSAA on the scene: stable edges while the camera moves (FXAA works per frame and shimmers); FXAA stays as the
    // fallback where float MSAA is not available
    const msaa = renderer.capabilities.isWebGL2 && (renderer.extensions.has("EXT_color_buffer_float") || renderer.extensions.has("EXT_color_buffer_half_float")) ? 4 : 0;
    this.scene = target(true, msaa);
    this.scene.texture.name = "SolRun.scene";
    for (let i = 0; i < 4; i++) this.levels.push(target(false));
    const material = (fragmentShader: string, uniforms: Record<string, THREE.IUniform>, extra: Partial<THREE.ShaderMaterialParameters> = {}) =>
      new THREE.ShaderMaterial({ vertexShader: FULLSCREEN_VERTEX, fragmentShader, uniforms, depthTest: false, depthWrite: false, toneMapped: false, ...extra });
    this.prefilter = material(PREFILTER, { tScene: { value: this.scene.texture }, texel: { value: new THREE.Vector2() }, threshold: { value: BLOOM_THRESHOLD }, knee: { value: 0.5 } });
    this.down = material(DOWN, { tSource: { value: null }, texel: { value: new THREE.Vector2() } });
    this.up = material(UP, { tSource: { value: null }, texel: { value: new THREE.Vector2() }, weight: { value: 1 } }, { blending: THREE.AdditiveBlending, transparent: true });
    this.final = material(FINAL, {
      tScene: { value: this.scene.texture }, tBloom: { value: this.levels[0].texture }, texel: { value: new THREE.Vector2() }, bloomStrength: { value: 0.55 },
      toneMappingExposure: { value: 1 }, saturation: { value: 1.25 }, contrast: { value: 1.06 }, vignette: { value: 0.2 }, aspect: { value: 16 / 9 },
      fxaa: { value: msaa > 0 ? 0 : 1 }
    });
    const size = renderer.getSize(new THREE.Vector2());
    this.setSize(size.x, size.y, renderer.getPixelRatio());
  }

  /** How strongly the bloom is added (day 0.55; night.ts asks for a little more after dark). */
  setBloomStrength(strength: number): void {
    this.final.uniforms.bloomStrength.value = strength;
  }

  /** The HDR target the world is drawn into (the warm-up compiles shaders against it). */
  get sceneTarget(): THREE.WebGLRenderTarget {
    return this.scene;
  }

  setSize(width: number, height: number, pixelRatio: number): void {
    this.width = Math.max(1, Math.round(width * pixelRatio));
    this.height = Math.max(1, Math.round(height * pixelRatio));
    this.scene.setSize(this.width, this.height);
    let w = this.width, h = this.height;
    for (const level of this.levels) {
      w = Math.max(1, Math.round(w / (level === this.levels[0] ? 4 : 2)));
      h = Math.max(1, Math.round(h / (level === this.levels[0] ? 4 : 2)));
      level.setSize(w, h);
    }
    (this.final.uniforms.texel.value as THREE.Vector2).set(1 / this.width, 1 / this.height);
    this.final.uniforms.aspect.value = Math.max(0.3, width / Math.max(1, height));
    (this.prefilter.uniforms.texel.value as THREE.Vector2).set(1 / this.width, 1 / this.height);
  }

  render(_dt: number): void {
    const renderer = this.renderer;
    renderer.setRenderTarget(this.scene);
    renderer.render(this.world, this.camera);
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;                                    // full-screen passes overwrite; the up passes add
    // bloom: prefilter to 1/4, down to 1/8, 1/16, 1/32, then back up, each level added onto the one above
    this.pass(this.prefilter, this.levels[0]);
    for (let i = 1; i < this.levels.length; i++) {
      const source = this.levels[i - 1];
      this.down.uniforms.tSource.value = source.texture;
      (this.down.uniforms.texel.value as THREE.Vector2).set(0.5 / source.width, 0.5 / source.height);
      this.pass(this.down, this.levels[i]);
    }
    for (let i = this.levels.length - 1; i > 0; i--) {
      const source = this.levels[i];
      this.up.uniforms.tSource.value = source.texture;
      (this.up.uniforms.texel.value as THREE.Vector2).set(0.5 / source.width, 0.5 / source.height);
      this.pass(this.up, this.levels[i - 1]);
    }
    this.final.uniforms.toneMappingExposure.value = renderer.toneMappingExposure;
    this.pass(this.final, null);
    renderer.autoClear = autoClear;
  }

  private pass(material: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget | null): void {
    this.renderer.setRenderTarget(target);
    this.quad.material = material;
    this.quad.render(this.renderer);
  }

  dispose(): void {
    this.scene.dispose();
    for (const level of this.levels) level.dispose();
    for (const material of [this.prefilter, this.down, this.up, this.final]) material.dispose();
    this.quad.dispose();
  }
}
